import { useCallback, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';

import {
  ApiFormattedException,
  DevicesCategory,
  ModeratedTaskSetting,
  Participation,
  SessionQuota,
  useBookMeeting,
  useHaveQuota,
  usePaginatedAvailableDates,
  useSession,
  useTaskSetting,
} from '@app/api';
import {
  CenteredModal,
  CheckboxBlockField,
  CheckboxBlockList,
  InformationBox,
  useParticipationDisplayName,
  useRequestBillingInformation,
  useToast,
} from 'components';
import { useTranslation } from 'react-i18next';
import { MeetingDateForm } from './MeetingDateForm';
import { DEVICE_TYPE_ICONS } from 'pages/Dashboard/EditSession/components/SessionTechnicalNeeds';

const useStyles = createUseStyles((theme: Theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 3 + 'px',
  },
  deviceChoiceLabel: {
    ...theme.fonts.body,
  },
  deviceListContainer: {
    '& label': {
      fontSize: theme.fonts.body.fontSize,
    },
  },
  deviceList: {
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 3,
  },
  deviceChoice: {
    minWidth: 0,
    marginBottom: 0,
  },
  errorText: {
    ...theme.fonts.caption,
    color: theme.colors.red,
    marginTop: theme.marginBase,
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose: (isCancelled: boolean) => any;
  participation?: Participation;
}

const initialValues = {
  date: '',
};

export const BookMeetingModal = ({
  participation,
  isOpen,
  onRequestClose,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();
  const [selectedDevice, setSelectedDevice] = useState<DevicesCategory | null>(
    null,
  );
  const [deviceError, setDeviceError] = useState<string>('');
  const { t } = useTranslation();

  const { data: session } = useSession(participation?.sessionId);
  const { data: taskSetting } = useTaskSetting<ModeratedTaskSetting>(
    session?.taskSettingId,
  );
  const paginatedAvailableDates = usePaginatedAvailableDates(
    session?.taskSettingId,
  );
  const haveDeviceQuota = useHaveQuota(
    session?.id,
    SessionQuota.ALLOWED_DEVICES_CATEGORY,
  );
  const displayDeviceSelect =
    haveDeviceQuota && !!taskSetting?.allowedDevices?.length;

  const { mutateAsync: book } = useBookMeeting(participation?.id);
  const requestBillingInformation = useRequestBillingInformation();

  const handleDeviceChange = useCallback(
    (device: DevicesCategory) => {
      if (selectedDevice === device) {
        setSelectedDevice(null);
      } else {
        setSelectedDevice(device);
        setDeviceError('');
      }
    },
    [selectedDevice],
  );

  const submit = useCallback(
    async (values: { date: string }) => {
      if (displayDeviceSelect && !selectedDevice) {
        setDeviceError(t('sessions.details.appointmentBooking.deviceRequired'));
        return;
      }

      try {
        if (!participation) {
          return;
        }

        await book({
          date: values.date,
          selectedDevice: selectedDevice,
        });
        toast.success(t('sessions.details.appointmentBooking.toaster'));
        onRequestClose(false);
      } catch (e) {
        if (
          e instanceof ApiFormattedException &&
          e.getCode() === 'INCOMPLETE_BILLING_INFO'
        ) {
          await requestBillingInformation();
          await submit(values);
          return;
        }
        toast.exception(e);
        throw e;
      }
    },
    [
      book,
      toast,
      onRequestClose,
      requestBillingInformation,
      participation,
      t,
      selectedDevice,
      displayDeviceSelect,
    ],
  );

  const participantName = useParticipationDisplayName(participation);

  if (!participation) {
    return null;
  }
  return (
    <CenteredModal
      isOpen={isOpen}
      title={t('sessions.details.appointmentBooking.title', {
        participant: participantName,
      })}
      onRequestClose={() => onRequestClose(true)}
    >
      <div className={classes.content}>
        <InformationBox
          title={t('sessions.details.appointmentBooking.warn.title')}
          text={t('sessions.details.appointmentBooking.warn.message')}
          variant="danger"
        />

        {taskSetting && (
          <div>
            {displayDeviceSelect && (
              <div className={classes.deviceListContainer}>
                <CheckboxBlockList
                  label={t('sessions.details.appointmentBooking.deviceChoice')}
                  classNameList={classes.deviceList}
                  checkboxClassName={classes.deviceChoice}
                >
                  {taskSetting?.allowedDevices?.map((device) => (
                    <div key={device}>
                      <CheckboxBlockField
                        label={t(
                          `sessions.display.allowedDevicesCategoriesChoices.${device}`,
                        )}
                        value={device}
                        checked={device === selectedDevice}
                        type="radio"
                        variant="green"
                        icon={DEVICE_TYPE_ICONS[device]}
                        onChange={() => handleDeviceChange(device)}
                      />
                    </div>
                  ))}
                </CheckboxBlockList>
              </div>
            )}
            {deviceError && (
              <div className={classes.errorText}>{deviceError}</div>
            )}
            <MeetingDateForm
              duration={taskSetting?.appointmentDuration}
              onRequestClose={() => onRequestClose(true)}
              initialValues={initialValues}
              paginatedAvailableDates={paginatedAvailableDates}
              onSubmit={submit}
            />
          </div>
        )}
      </div>
    </CenteredModal>
  );
};
